import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { observerApi } from "../services/observerApi";
const titles: Record<string, string> = {
  nomination: "Nominate a public practitioner",
  self: "Submit your own public practice",
  dispute: "Dispute a review",
  reply: "Reply to a review",
  correction: "Suggest a factual correction",
  takedown: "Request suppression",
  impersonation: "Report impersonation",
  extortion: "Report extortion or coercion",
  appeal: "Appeal a decision",
  transparency_response: "Respond to transparency observations",
};
export default function ObserverSubmitPage() {
  const [params] = useSearchParams(),
    kind = params.get("kind") || "correction";
  const [done, setDone] = useState(""),
    [error, setError] = useState(""),
    [form, setForm] = useState<Record<string, string>>({
      entity: params.get("entity") || "",
      review: params.get("review") || "",
      request: params.get("request") || "",
      assertion: params.get("assertion") || "",
      platformAccount: params.get("platformAccount") || "",
      displayName: "",
      entityType: "individual",
      practiceTags: "",
      url: "",
      submitter: "",
      grounds: "",
      details: "",
      subjectMethod: "social_code",
      subjectTarget: "",
      subjectAccount: "",
      verificationId: "",
      verificationCode: "",
    });
  const set = (k: string, v: string) => setForm((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    if (kind === "correction") observerApi.event("correction_started");
  }, [kind]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const candidate = {
        ...form,
        practiceTags: form.practiceTags
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
      };
      if (kind === "nomination") {
        await observerApi.nominate(candidate);
        observerApi.event("nomination_submitted");
      } else if (kind === "self") await observerApi.selfSubmit(candidate);
      else if (kind === "correction") {
        await observerApi.correction(form.entity, {
          grounds: form.grounds,
          details: form.details,
          assertionId: form.assertion,
        });
        observerApi.event("correction_submitted");
      } else if (kind === "dispute") {
        await observerApi.dispute(form.review, {
          grounds: form.grounds,
          details: form.details,
        });
        observerApi.event("dispute_opened");
      } else if (kind === "reply")
        await observerApi.reply(form.review, form.details);
      else if (kind === "takedown")
        await observerApi.takedown(form.entity, {
          grounds: form.grounds,
          details: form.details,
        });
      else if (kind === "impersonation") {
        await observerApi.impersonation(form.entity, {
          grounds: form.grounds,
          details: form.details,
          platformAccountId: form.platformAccount,
        });
        observerApi.event("impersonation_reported");
      } else if (kind === "extortion")
        await observerApi.extortion(form.entity, {
          grounds: form.grounds,
          details: form.details,
        });
      else if (kind === "appeal")
        await observerApi.appeal(form.request, form.grounds);
      else if (kind === "transparency_response")
        await observerApi.transparencyResponse(form.entity, form.details);
      observerApi.event("report_submitted");
      setDone(
        "Submitted. The request is now in the appropriate human-review queue.",
      );
    } catch (x: any) {
      setError(x?.response?.data?.error || "Submission failed.");
    }
  };
  const candidate = kind === "nomination" || kind === "self";
  const subjectAction = kind === "reply" || kind === "dispute" || kind === "transparency_response";
  const candidateIntro =
    kind === "self"
      ? "Tell us where your public practice lives online. Manifestation, law of attraction, law of assumption, tarot, psychic, mediumship, astrology, energy, spiritual teaching, and related transformational practices are all within scope. Submission is simple; it does not claim or publish a profile."
      : "Help us find a missing public teacher, coach, reader, psychic, medium, astrologer, creator, or spiritual practice. A nomination is a research lead, not an endorsement or accusation.";
  return (
    <div className="ot-page">
      <header className="ot-page-head">
        <span className="section-kicker">
          {candidate ? "Spiritual-practice profile intake" : "Report a problem"}
        </span>
        <h1>{titles[kind] || "Report a problem"}</h1>
        <p>
          {candidate
            ? candidateIntro
            : "Corrections, disputes, takedowns, appeals, and replies are free. Payment or a claimed profile is never required."}
        </p>
      </header>
      {done ? (
        <div className="ot-empty">
          <h2>Thank you</h2>
          <p>{done}</p>
        </div>
      ) : (
        <form className="ot-form" onSubmit={submit}>
          {subjectAction &&
            !localStorage.getItem("observer_subject_session") && (
              <fieldset>
                <legend>Verify that this profile is about you</legend>
                <p>
                  This channel-control check is free and does not claim the
                  profile.
                </p>
                <label>
                  Verification method
                  <select
                    value={form.subjectMethod}
                    onChange={(e) => set("subjectMethod", e.target.value)}
                  >
                    <option value="social_code">
                      Code on an official public channel
                    </option>
                    <option value="domain_email">
                      Email on the official domain
                    </option>
                  </select>
                </label>
                <label>
                  Your email
                  <input
                    required
                    type="email"
                    value={form.subjectAccount}
                    onChange={(e) => set("subjectAccount", e.target.value)}
                  />
                </label>
                <label>
                  {form.subjectMethod === "social_code"
                    ? "Already-linked official website or channel"
                    : "Email on the already-linked official domain"}
                  <input
                    required
                    value={form.subjectTarget}
                    onChange={(e) => set("subjectTarget", e.target.value)}
                  />
                </label>
                {!form.verificationId ? (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await observerApi.startSubjectVerification(
                          form.entity,
                          {
                            account: form.subjectAccount,
                            method: form.subjectMethod,
                            target: form.subjectTarget,
                          },
                        );
                        set("verificationId", r.verificationId);
                        setDone("");
                        if (r.instructions) setError(r.instructions);
                      } catch (x: any) {
                        setError(
                          x?.response?.data?.error ||
                            "Verification could not be started.",
                        );
                      }
                    }}
                  >
                    Start free verification
                  </button>
                ) : (
                  <>
                    <label>
                      Verification code
                      <input
                        required
                        value={form.verificationCode}
                        onChange={(e) =>
                          set("verificationCode", e.target.value)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await observerApi.verifySubject(
                            form.verificationId,
                            form.verificationCode,
                          );
                          setError("");
                        } catch (x: any) {
                          setError(
                            x?.response?.data?.error || "Verification failed.",
                          );
                        }
                      }}
                    >
                      Verify control
                    </button>
                  </>
                )}
              </fieldset>
            )}
          {candidate ? (
            <>
              <label>
                Public name or practice name
                <input
                  required
                  value={form.displayName}
                  onChange={(e) => set("displayName", e.target.value)}
                  placeholder="Name used for teachings, readings, content, or services"
                />
              </label>
              <label>
                Profile subject
                <select
                  value={form.entityType}
                  onChange={(e) => set("entityType", e.target.value)}
                >
                  <option value="individual">
                    Individual teacher or practitioner
                  </option>
                  <option value="brand">Public brand or practice</option>
                  <option value="organisation">
                    Organisation or community
                  </option>
                </select>
              </label>
              <label>
                Practices and teaching themes
                <input
                  required
                  value={form.practiceTags}
                  onChange={(e) => set("practiceTags", e.target.value)}
                  placeholder="e.g. law of attraction, manifestation, tarot, mediumship"
                />
                <small>Use a few plain terms separated by commas.</small>
              </label>
              <label>
                Primary public website or official channel
                <input
                  required
                  type="url"
                  value={form.url}
                  onChange={(e) => set("url", e.target.value)}
                  placeholder="https://"
                />
                <small>
                  This can be an official website, YouTube channel, public
                  social profile, course page, reading page, or other public
                  home for the practice.
                </small>
              </label>
              <label>
                Your email
                <input
                  required
                  type="email"
                  value={form.submitter}
                  onChange={(e) => set("submitter", e.target.value)}
                />
                <small>
                  Used for submission updates and research questions; never
                  displayed on the public profile.
                </small>
              </label>
            </>
          ) : (
            <>
              <label>
                Grounds
                <input
                  required
                  value={form.grounds}
                  onChange={(e) => set("grounds", e.target.value)}
                />
              </label>
              {kind !== "appeal" && (
                <label>
                  Facts and supporting detail
                  <textarea
                    required
                    value={form.details}
                    onChange={(e) => set("details", e.target.value)}
                  />
                </label>
              )}
            </>
          )}
          {error && <p className="ot-error">{error}</p>}
          <button className="button button-ink">
            {candidate ? "Send for research" : "Submit"}
          </button>
        </form>
      )}
    </div>
  );
}
